/**
 * La pantalla de quien todavía no tiene ningún sitio.
 *
 * Es lo que ve un usuario recién registrado y también quien acaba de borrar su
 * último proyecto. Antes ahí había un panel con un menú de dos entradas y una
 * lista vacía: navegación para recorrer algo que no existe.
 *
 * Sólo la barra superior y una forma principal de empezar: describir el sitio
 * para que lo construya la IA. Plantilla y lienzo vacío quedan debajo como
 * alternativas, visibles pero sin competir con el arranque recomendado.
 */
import { AiOutCard } from './Assistant/AiOutCard'
import { useEffect, useRef, useState } from 'react'
import { IconArrowRight, IconLayoutBoard, IconPencil, IconPlus, IconPointerFilled, IconSquare, IconX } from '@tabler/icons-react'
import { AiMascot } from './CreateWithAiModal/AiMascot'
import { MIN_PROMPT, PROMPT_TEMPLATES } from './CreateWithAiModal/promptConfig'
import { PromptComposer } from './CreateWithAiModal/PromptComposer'
import { IdeaShuffle } from './CreateWithAiModal/IdeaShuffle'
import { topNotice, usePendingNotices } from '../lib/pendingNotices'
import { useI18n } from '../lib/i18n'
import { getAccountUsage, type BillingPlan } from '../lib/api'
import { aiDraftName, clearAiDraft, readAiDraft } from '../lib/aiDraftStore'
import { readWelcome, saveWelcome, welcomeDone, type WelcomeAnswers } from '../lib/welcomeAnswers'
import { industryIdeas } from '../lib/promptIdeas'
import { useWizardPhotos, wizardPhotoLimit } from '../lib/wizardPhotos'
import { WelcomeWizard } from './WelcomeWizard'

type Option = {
  key: 'template' | 'blank'
  icon: typeof IconLayoutBoard
  tone: string
  label: string
  description: string
}

/* Claves, no texto: constante de módulo. */
const OPTIONS: Option[] = [
  { key: 'template', icon: IconLayoutBoard, tone: 'azure', label: 'start.template', description: 'start.templateBody' },
  { key: 'blank', icon: IconSquare, tone: 'slate', label: 'start.blank', description: 'start.blankBody' },
]

export function StartProjectScreen({ onStart, userId, token, flowOpen = false, startPrompt, onStartPromptUsed, carried, onDropCarried, onOpenPlans }: {
  /** A Planes, desde la tarjeta de «sin generaciones». */
  onOpenPlans?: () => void
  /**
   * Lo que se trae de veltica.com («Diseña esta página»): el estilo elegido,
   * dicho en una línea, y si además se trabajó la página. Sólo con la página
   * trabajada se enseña algo —una tercera tarjeta, la primera, para seguir con
   * ella—; el estilo solo va por dentro, sin aviso.
   */
  carried?: {
    label: string
    hasContent: boolean
    /**
     * Si la página viene del ejemplo de la IA de veltica.com («Café Brasa»):
     * su nombre en la tarjeta, su descripción a un toque en la caja de IA y su
     * tipo de negocio ya marcado en la bienvenida.
     */
    example?: { name: string; prompt: string; business: string; goals: string[] } | undefined
  } | undefined
  /** Empezar sin eso. */
  onDropCarried?: (() => void) | undefined
  /**
   * Lo que ya se escribió antes de llegar —en la caja de IA de veltica.com—:
   * se genera con ello sin volver a pedirlo. A quien es nuevo se le pregunta
   * antes la industria y lo que quiere del sitio (la bienvenida), que es lo
   * único que falta para generar bien; a quien ya lo contestó, directo.
   */
  startPrompt?: string | undefined
  /** Ya se arrancó con `startPrompt`: quien lo trajo puede olvidarlo. */
  onStartPromptUsed?: (() => void) | undefined
  /** `resume`: seguir con el borrador de IA guardado (`aiDraftStore`). */
  onStart: (option: 'ai' | 'resume' | 'site' | Option['key'], prompt?: string) => void
  userId?: string
  token?: string
  /**
   * Si hay un camino abierto encima. Esta pantalla se queda montada debajo
   * del asistente: al cerrarlo sin crear se vuelve a leer el borrador —y lo
   * que queda de generaciones—, que pudo cambiar mientras tanto.
   */
  flowOpen?: boolean
}) {
  const { t, fmt } = useI18n()
  /* El borrador que quedó sin crear, si lo hay: se ofrece seguir con él en vez
     de generar otro. */
  const [savedDraft, setSavedDraft] = useState(() => readAiDraft(userId))
  useEffect(() => { if (!flowOpen) setSavedDraft(readAiDraft(userId)) }, [flowOpen, userId])
  /* Cuántas generaciones le quedan, para decirlo antes de empezar. */
  const [creditsLeft, setCreditsLeft] = useState<{ left: number; total: number; monthly: boolean } | null>(null)
  /* El plan, para saber cuántas fotos propias admite (`wizardPhotos`). */
  const [plan, setPlan] = useState<BillingPlan | null>(null)
  useEffect(() => {
    if (!token) return
    let alive = true
    void getAccountUsage(token)
      .then((usage) => {
        if (alive) setPlan(usage.plan)
        const limit = usage.limits.ai_credits
        if (!alive || limit === null) return
        setCreditsLeft({ left: Math.max(0, limit - usage.used.aiCredits), total: limit, monthly: usage.aiCreditsResetAt !== null })
      })
      .catch(() => {})
    return () => { alive = false }
  }, [token, flowOpen])
  const waiting = topNotice(usePendingNotices()) !== null
  const [prompt, setPrompt] = useState('')
  /* La bienvenida, una vez por usuario y sólo a quien no ha empezado nada: con
     un borrador guardado ya sabe lo que quiere. Ver `WelcomeWizard`. */
  const [welcome, setWelcome] = useState(() => !welcomeDone(userId) && !readAiDraft(userId))
  /* Las respuestas se guardan y nada más: el prompt sigue vacío para que lo
     escriba quien crea. Viajan a la IA aparte, como contexto (ver
     `welcomeBrief`), y ordenan los templates por su industria. */
  function finishWelcome(answers: WelcomeAnswers | null) {
    saveWelcome(userId, answers)
    setAnswers(answers)
    setWelcome(false)
  }
  /* Con el texto ya escrito: en cuanto no queda bienvenida por contestar —al
     llegar o al terminarla—, a generar. Una sola vez. */
  const startedWithPrompt = useRef(false)
  useEffect(() => {
    if (!startPrompt || welcome || startedWithPrompt.current) return
    startedWithPrompt.current = true
    onStartPromptUsed?.()
    onStart('ai', startPrompt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startPrompt, welcome])
  /* Las ideas de arranque: las de su industria si la dijo; si no, las de
     siempre. Ver `promptIdeas`. */
  const [answers, setAnswers] = useState(() => readWelcome(userId))
  /* Cambiadas en las fichas de debajo del campo: se guardan al momento, que
     es lo que lee el asistente al generar y lo que sale la próxima vez. */
  function changeAnswers(next: WelcomeAnswers) {
    saveWelcome(userId, next)
    setAnswers(next)
  }
  /* Las fotos propias para la IA. El asistente, encima, pudo cambiarlas: al
     cerrarlo se releen. */
  const photos = useWizardPhotos(userId, token, wizardPhotoLimit(plan))
  const reloadPhotos = photos.reload
  useEffect(() => { if (!flowOpen) reloadPhotos() }, [flowOpen, reloadPhotos])
  const ownIdeas = industryIdeas(answers?.business)
  const ideas = ownIdeas ?? PROMPT_TEMPLATES
  const promptLength = prompt.trim().length
  /* El campo crece solo con lo que se escribe (ver `PromptComposer`). */
  const fieldRef = useRef<HTMLTextAreaElement>(null)
  /* Un ejemplo pedido con «Prueba un ejemplo» aparece palabra a palabra. */
  const [revealKey, setRevealKey] = useState(0)
  function showExample(text: string) {
    setPrompt(text)
    setRevealKey((key) => key + 1)
    fieldRef.current?.focus()
  }

  function startWithAi() {
    if (promptLength < MIN_PROMPT) return
    onStart('ai', prompt.trim())
  }

  if (welcome) {
    return (
      <main className="dash-start-screen is-welcome">
        <WelcomeWizard onDone={finishWelcome} initial={carried?.example} />
      </main>
    )
  }

  return (
    /* Escribiendo, las otras dos salidas se encogen a dos franjas: siguen ahí,
       pero el protagonista es el texto. */
    <main className={`dash-start-screen${promptLength > 0 ? ' is-typing' : ''}`}>
      <section className="dash-start-ai" aria-labelledby="dash-start-ai-title">
        {/* La mascota recibe. Es lo primero que ve alguien que acaba de entrar y
            todavía no tiene nada suyo en pantalla: da con quién estar antes de dar
            qué hacer. */}
        {/* Y mira hacia arriba cuando hay algo esperando ahí: en esta pantalla los
            avisos son casi siempre una invitación o un traspaso, o sea la única
            forma que tiene alguien sin proyectos de acabar teniendo uno. Que la
            mascota los señale es más útil aquí que en ningún otro sitio. */}
        {/* Al LADO del título y no encima: es la misma presentación del asistente
            de IA, pero aquí debajo hay un campo, cinco sugerencias y dos tarjetas
            más, y una mascota centrada sobre el título empujaba todo eso fuera de
            la pantalla. Sentada a la izquierda ocupa el alto que el texto ya
            gastaba. */}
        <header className="dash-start-intro">
          <AiMascot gaze={waiting ? 'up' : undefined} />
          <div className="cwa-prompt-copy">
            <h1 id="dash-start-ai-title">{t('cwa.title')}</h1>
            <span className="dash-start-ideas">
              {carried?.example?.prompt ? (
                <button type="button" className="cwa-idea-shuffle" onClick={() => showExample(carried.example!.prompt)}>
                  <span>{t('start.describeExample', { name: carried.example.name })}</span>
                </button>
              ) : null}
              <IdeaShuffle ideas={ideas} onIdea={showExample} />
            </span>
          </div>
        </header>


        {savedDraft ? (
          <div className="dash-start-resume" role="status">
            <span className="dash-topbar-pending-dot" aria-hidden="true" />
            <span className="dash-start-resume-copy">
              <strong>{t('start.resume.title', { name: aiDraftName(savedDraft) || t('cwa.defaultName') })}</strong>
              <small>{t('start.resume.body', { when: fmt.relative(savedDraft.savedAt) ?? '' })}</small>
            </span>
            <span className="dash-start-resume-actions">
              <button type="button" className="dash-secondary-btn" onClick={() => { clearAiDraft(userId); setSavedDraft(null) }}>{t('start.resume.discard')}</button>
              <button type="button" className="dash-primary-btn" onClick={() => onStart('resume')}>{t('start.resume.go')}</button>
            </span>
          </div>
        ) : null}

        {/* Sin generaciones, no hay caja: escribir la idea para que la
            rechacen al pulsar se siente como un fallo. Las plantillas y la
            página en blanco siguen debajo. */}
        {creditsLeft && creditsLeft.left === 0 ? (
          <AiOutCard title={t('ai.gen.out')} body={t('ai.gen.outHint')} onOpenPlans={() => onOpenPlans?.()} className="is-start" />
        ) : (
        <PromptComposer
          value={prompt}
          fieldRef={fieldRef}
          ariaLabel={t('start.promptAria')}
          onChange={setPrompt}
          revealKey={revealKey}
          onSubmit={startWithAi}
          brief={answers}
          onBrief={changeAnswers}
          photos={token ? photos : undefined}
          /* Antes de empezar, cuánto cuesta: nada por generar y probar; una al
             crear el sitio. Llega después que la pantalla: hasta entonces, la
             frase de siempre. */
          note={creditsLeft
            ? t(creditsLeft.left > 0
              ? (creditsLeft.monthly ? 'start.credits.monthly' : 'start.credits.total')
              : 'start.credits.none', { left: creditsLeft.left, total: creditsLeft.total })
            : undefined}
        />
        )}
      </section>

      <div className={`dash-start-options${carried?.hasContent ? ' has-three' : ''}`}>
        {/* La página que se editó en veltica.com, la primera de las tres: con
            su miniatura de ventana de navegador —no el dibujo de «plantilla»,
            con el que se confundía— y su texto diciendo de dónde viene.

            Sólo con la página trabajada. Quien sólo eligió un estilo no ve
            nada aquí: el estilo se le aplica igual, por dentro, empiece por
            donde empiece (`startRecipe` en `Home`), sin un aviso que atender. */}
        {carried?.hasContent ? (
          <div className="dash-start-option-site">
            <button type="button" className="dash-start-option" onClick={() => onStart('site')}>
              <span className="dash-start-option-head">
                <SitePageArt />
                <strong>{carried.example ? t('start.siteNamed', { name: carried.example.name }) : t('start.site')}</strong>
              </span>
              <span className="dash-start-option-desc">
                {carried.example
                  ? t('start.siteBodyExample', { name: carried.example.name })
                  : carried.label ? t('start.siteBodyStyle', { design: carried.label }) : t('start.siteBody')}
              </span>
              <span className="dash-secondary-btn dash-start-option-go">
                {t('start.siteGo')} <IconArrowRight size={14} aria-hidden="true" />
              </span>
              <span className="dash-start-option-arrow" aria-hidden="true"><IconArrowRight size={15} /></span>
            </button>
            {/* Fuera del botón de la tarjeta —un botón no va dentro de otro—:
                descartar lo traído y quedarse con los otros dos caminos. */}
            <button type="button" className="dash-start-option-drop" aria-label={t('start.siteDrop')} title={t('start.siteDrop')} onClick={() => onDropCarried?.()}>
              <IconX size={13} aria-hidden="true" />
            </button>
          </div>
        ) : null}
        {OPTIONS.map((option) => {
          const Icon = option.icon
          return (
            <button
              type="button"
              key={option.key}
              className="dash-start-option"
              onClick={() => onStart(option.key)}
            >
              {/* El icono y el título, uno junto al otro en las dos formas: al
                  pasar a franja sólo se encogen, por CSS, en su sitio. */}
              <span className="dash-start-option-head">
                {option.key === 'template' ? <TemplateArt /> : option.key === 'blank' ? <BlankArt /> : (
                  <span className={`dash-widget-glass-icon dash-sidebar-glass-icon dash-start-option-icon is-${option.tone}`} aria-hidden="true">
                    <Icon size={17} />
                  </span>
                )}
                <strong>{t(option.label)}</strong>
              </span>
              <span className="dash-start-option-desc">{t(option.description)}</span>
              {/* El botón blanco del panel, no un enlace de color: la tarjeta
                  entera es lo que se pulsa, así que esto es la señal de que
                  hay algo que pulsar. `span` y no `button` — dentro de un
                  botón no puede ir otro. */}
              <span className="dash-secondary-btn dash-start-option-go">
                {t('start.go')} <IconArrowRight size={14} aria-hidden="true" />
              </span>
              {/* En la franja, una flecha propia que aparece: no es el botón
                  que viaja, que se veía extraño encogiéndose. */}
              <span className="dash-start-option-arrow" aria-hidden="true"><IconArrowRight size={15} /></span>
            </button>
          )
        })}
      </div>
    </main>
  )
}

/**
 * La página que se editó en veltica.com, en miniatura: una ventana de
 * navegador con su barra, un titular, unas líneas y un botón, y el lápiz de
 * «la tocaste tú». Dice «una página concreta, ya empezada», que es justo lo que
 * la distingue de elegir una plantilla.
 */
/** La dirección de la demo, tal cual: es un nombre, no un texto que traducir. */
const DEMO_HOST = 'veltica.com'

function SitePageArt() {
  return (
    <span className="dash-start-page-art" aria-hidden="true">
      <span className="dash-start-page-art-bar"><i /><i /><i /><b>{DEMO_HOST}</b></span>
      <span className="dash-start-page-art-body"><u /><s /><s /><em /></span>
      <span className="dash-start-page-art-pen"><IconPencil size={11} stroke={2.4} /></span>
    </span>
  )
}

/**
 * Un diseño en miniatura: una tarjeta, la letra, un botón y el cursor que va a
 * pulsarlo. Es el mismo dibujo de «Combinaciones» en el editor, que dice «un
 * diseño hecho que cambias a tu gusto» mejor que un icono de rejilla. En los
 * colores del panel, no en los de un sitio.
 */
function TemplateArt() {
  return (
    <span className="dash-start-art" aria-hidden="true">
      <span className="dash-start-art-frame">
        <span className="dash-start-art-card"><i /><i /></span>
        <b className="dash-start-art-type">Aa</b>
        <span className="dash-start-art-button" />
        <IconPointerFilled size={13} className="dash-start-art-cursor" />
        <span className="dash-start-art-spark" />
      </span>
    </span>
  )
}

/**
 * Su pareja para «En blanco»: el mismo marco, pero vacío —un lienzo punteado
 * con un «+» en medio y el cursor yendo a ponerle lo primero—.
 */
function BlankArt() {
  return (
    <span className="dash-start-art" aria-hidden="true">
      <span className="dash-start-art-frame is-blank">
        <span className="dash-start-art-canvas"><IconPlus size={12} stroke={2.4} /></span>
        <IconPointerFilled size={13} className="dash-start-art-cursor" />
      </span>
    </span>
  )
}
